import { useEffect, useRef, useState } from "react"
import reactHtmlParser from "react-html-parser"
import Box from "component-lib/Base/components/Box"
import { Typography } from "component-lib/Base/components/Typography/Typography"
import { makeStyles } from "tss-react/mui"
import {
  BOOTSTRAP_GRID_MD_MIN,
  BOOTSTRAP_GRID_IPAD_PRO_MAX,
} from "constants-lib/dimensions"
import { YES } from "constants-lib/common"
import { pushToAdobeDataLayer } from "utils-lib/analytics/adobeDataLayer"
import get from "lodash/get"
import { COLORS } from "constants-lib/colors"
import { getWindow } from "utils-lib/getWindow"
import { isLocal, appEnvVars } from "utils-lib/builds"
import { isPdfUrl } from "utils-lib/validation/validateHashTagUrl"
import withRouter from "hoc-lib/router"
import { withAuthentication } from "hoc-lib/appAuthentication"
import { fakeAnalyticsClick } from "utils-lib/analytics/fakeAnalyticsClick"
import { FormattedTextField, ButtonPrimary, ButtonArrow } from "../../Base"
import "./SingleColumnText.scss"

const ssrWindow = getWindow()

const useStyles = makeStyles()(
  (theme, { colorPalette, isGreenBG, supportPage }) => {
    const classStyles = {}

    // handle setting values for root class
    let bgColor = "inherit"

    if (colorPalette.toLowerCase() === COLORS.SAND) {
      bgColor = theme.palette.background.sand
    } else if (isGreenBG) {
      bgColor = theme.palette.primary.main2_0
    }

    classStyles.root = {
      bgColor,
    }

    return {
      root: {
        backgroundColor: classStyles.root.bgColor,
      },
      supportcheck: {
        padding: "0",
        background: `inherit`,
        "& .innercontainer": {
          padding: `24px 0 40px 0`,
          background: `inherit`,
          [theme.breakpoints.down("lg")]: {
            padding: `24px 25px 39px 25px`,
          },
        },
        [theme.breakpoints.down("lg")]: {
          padding: "0",
          background: `inherit`,
        },

        "& .supportCheckBody": {
          paddingLeft: "24px",
          fontSize: `18px !important`,
          lineHeight: `28px`,
          "& p": {
            marginBottom: `24px`,
            [theme.breakpoints.down("lg")]: {
              lineHeight: `24px`,
              marginBottom: 0,
            },
          },
          [theme.breakpoints.down("lg")]: {
            padding: `0 25px 16px 25px`,
            fontSize: `1rem !important`,
          },
        },
        "& .supportCheckButton": {
          display: `block !important`,
          padding: "0 31px 27px 25px !important",
          [theme.breakpoints.down("lg")]: {
            padding: "0 31px 25px 20px !important",
          },
          "& button": {
            fontSize: `16px`,
            lineHeight: `16px`,
            padding: `16px 32px`,
            borderRadius: `48px !important`,
            [theme.breakpoints.down("lg")]: {
              width: `100%`,
              display: `block `,
              padding: `0`,
            },
          },
        },
      },
      supportInner: {
        background: `${theme.palette.common.white}`,
        margin: "0",
        borderRadius: `8px`,
        "& h3": {
          position: `relative`,
          left: `-0.2%`,
        },
        [theme.breakpoints.down("lg")]: {
          margin: "",
        },
        boxShadow: `0 2px 4px 0 ${theme.palette.border.grayboxShadow}`,
        border: `2px solid ${theme.palette.background.lightGray}`,
        "& .supportBlockHeading": {
          fontSize: `20px !important`,
          lineHeight: `24px`,
          borderRadius: "8px 0 0 0",
          fontFamily: "Maax-Bold !important",
          padding: "12px 24px 10px 24px",
          display: `inline-block`,
          background: `${theme.palette.secondary.shade1}`,
          color: `${theme.palette.common.white} !important`,
          [theme.breakpoints.down("lg")]: {
            display: `block`,
            lineHeight: `28px`,
            borderRadius: `8px 8px 0 0`,
            padding: `8px 24px`,
            fontSize: `18px !important`,
            marginBottom: 16,
          },
        },
      },
      innerContainer: {
        paddingTop: `2.5rem !important`,
        [theme.breakpoints.down("xl")]: {
          paddingTop: `2rem !important`,
        },
        [theme.breakpoints.down("lg")]: {
          paddingTop: `1.5rem !important`,
        },
      },
      horizontalScroll: {
        overflowX: `auto !important`,
      },
      heading: {
        "&&": {
          color:
            isGreenBG || supportPage
              ? theme.palette.common.white
              : theme.palette.primary.main2_0,
          marginBottom: `15px`,
          letterSpacing: `normal`,
          [theme.breakpoints.down(`sm`)]: {
            fontSize: `2rem`,
            lineHeight: `2rem`,
          },
        },
        "& ul": {
          listStyleType: "disc !important",
        },
      },
      subhead: {
        color: isGreenBG
          ? theme.palette.common.white
          : theme.palette.text.black,
        lineHeight: `1.5rem`,
        fontSize: `1.25rem`,
        [theme.breakpoints.down(`md`)]: {
          lineHeight: `1.125rem`,
          fontSize: `1rem`,
        },
      },
      body: {
        color: isGreenBG
          ? theme.palette.common.white
          : theme.palette.text.lightHeader,
        "& ul": {
          paddingLeft: `3rem`,
        },
      },
      singleColumnBody: {
        "& ul": {
          listStyleType: "disc !important",
        },
        "& a": {
          hoverStyleType: "disc",
        },
      },
      buttonContainer: {
        paddingTop: `0rem`,
        borderRadius: `28px !important`,
      },
      buttonAlignment: {
        flexDirection: "row",
        alignItems: "start",
        [theme.breakpoints.down("xl")]: {
          flexDirection: "column",
        },
      },
      fixedWidthSecondaryContainer: {
        [theme.breakpoints.up(`lg`)]: {
          maxWidth: 735,
        },
      },
      secondaryCtaSpacing: {
        [theme.breakpoints.down(`sm`)]: {
          marginTop: `0.5rem`,
        },
      },
      fixedWidthBody: {
        maxWidth: 633,
      },
    }
  },
)

/**
  Single-Column Text - Copy to introduce a section of the page.

  https://wastemanagement.atlassian.net/wiki/spaces/TDC/pages/1290502692/CL+ST+05+Single+Column+Text
*/
export const SingleColumnText = ({
  heading = "",
  isHeadingH1 = false,
  subhead = ``,
  body = ``,
  colorPalette = `white`,
  secondaryCtaText = "",
  secondaryCtaURL = "",
  secondaryCtaAnalyticsKey = "",
  primaryCtaText = "",
  primaryCtaURL = "",
  primaryCtaAnalyticsKey = "",
  linkStyle = `buttonwithoutarrows`,
  isExternalLinkPrimary = false,
  isExternalLinkSecondary = false,
  alignment = `left`,
  useFullWidthText = false,
  setModalId,
  router,
  data = {},
  isRecycleQuizPage,
  isQuizCompleted,
  feedbackButtonText = "",
  feedbackCaptureIndicator = "",
  supportPage = false,
  componentId = null,
  getPersonalization,
  enableTargetPersonalization = false,
  guidelines,
}) => {
  const bodyRef = useRef(null)
  const [innerWidth, setInnerWidth] = useState(null)
  const scrollContainerRef = useRef(null)
  const scrollAmount = 30 // Adjust the scroll speed for keyboard

  const hasFetched = useRef(false)
  const gisID = get(ssrWindow, `_dl.page.gisIDs[0]`, ``)

  const wasteAIClassification =
    get(guidelines, `wasteAIClassification`, "") === YES
  const wasteAIClassificationIsAllowed = wasteAIClassification

  const updateDimensions = () => {
    if (ssrWindow.innerWidth) setInnerWidth(ssrWindow.innerWidth)
  }

  useEffect(() => {
    updateDimensions(ssrWindow.innerWidth)
  }, [])

  useEffect(() => {
    if (enableTargetPersonalization) {
      if (!hasFetched.current && gisID) {
        hasFetched.current = true
        const fetchPersonalizationData = async () => {
          const guestPersonalizationAPI = await getPersonalization(gisID)
          const additionalData = {
            clicks: {
              object_content: {
                guestPersonalizationAPI,
              },
            },
          }
          pushToAdobeDataLayer("guestPersonalizationAPI", additionalData)
        }
        fetchPersonalizationData()
      }
    }
  }, [getPersonalization, gisID, enableTargetPersonalization])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!scrollContainerRef.current) return

      if (event.key === "ArrowLeft") {
        scrollContainerRef.current.scrollLeft -= scrollAmount
      } else if (event.key === "ArrowRight") {
        scrollContainerRef.current.scrollLeft += scrollAmount
      }
    }

    ssrWindow.addEventListener("keydown", handleKeyDown)

    return () => {
      ssrWindow.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  useEffect(() => {
    ssrWindow.addEventListener("resize", updateDimensions)
    return () => ssrWindow.removeEventListener("resize", updateDimensions)
  }, [])

  useEffect(() => {
    const isSupportPage = ssrWindow.location.pathname?.indexOf(`/support`) > -1
    // if modal is present on support pages, do not reset modalId in redux
    // use case: Request Support modal needs to persist on login refresh
    if (!isSupportPage) {
      if (typeof setModalId === `function`) {
        setModalId(``)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const el = bodyRef.current
    if (el) {
      const innerHTML = el.innerHTML.replace(/&nbsp;/g, " ")
      el.innerHTML = innerHTML
    }
  }, [bodyRef])

  const primaryClickHandler = (path, isExternalLink, e = null) => {
    if (isExternalLink && isExternalLink !== feedbackButtonText) {
      const pdfPath = isLocal() ? `${appEnvVars.mockOrigin}${path}` : path
      isPdfUrl(path)
        ? ssrWindow.open(pdfPath)
        : (ssrWindow.location.href = path)
    } else if (feedbackButtonText && isExternalLink === feedbackButtonText) {
      if (e) e.target.innerText = feedbackCaptureIndicator
    } else {
      router.push(path)
    }
  }
  const buttonIsLinkStylewithArrows =
    linkStyle.toLowerCase() === "linkstylewitharrows"
  const buttonIsLinkStyle = linkStyle.toLowerCase() === "linkstyle"
  const buttonIsWithArrow = linkStyle.toLowerCase() === "buttonwitharrows"
  const hasGreenBackground =
    colorPalette.toLowerCase() === `green1` ? `has-green-background` : ``
  const flexAlignment =
    alignment === `left` ? `start` : alignment === `right` ? `end` : alignment
  const isGreenBG = colorPalette.toLowerCase() === COLORS.GREEN1
  const { classes } = useStyles({
    isGreenBG,
    colorPalette,
    supportPage,
  })

  if (wasteAIClassificationIsAllowed) {
    return null
  }

  const analyticsCall = () => {
    if (isQuizCompleted) {
      fakeAnalyticsClick({
        dummyClass: `quizGameSubmitTracking`,
        analytics_key: `will-it-recycle-quiz-submit-entry`,
      })
    }
  }

  const renderButtonWithNWithoutArrows = (
    secondaryClickHandler,
    tertiaryClickHandler,
    hasFeedbackButtonText,
  ) => {
    const isDisabled = isRecycleQuizPage && !isQuizCompleted
    const isFormModal =
      tertiaryClickHandler &&
      ((primaryCtaURL && primaryCtaURL.toLowerCase().includes("form")) ||
        (primaryCtaAnalyticsKey &&
          primaryCtaAnalyticsKey.toLowerCase().includes("form")))

    return (
      <>
        {secondaryCtaText && secondaryCtaURL ? (
          <ButtonPrimary
            data-testid="SingleColumnText-ButtonPrimary"
            analytics_key={secondaryCtaAnalyticsKey || secondaryCtaText}
            href={secondaryClickHandler ? `` : secondaryCtaURL}
            isExternalLink={isExternalLinkSecondary}
            onClick={() =>
              secondaryClickHandler
                ? secondaryClickHandler()
                : primaryClickHandler(secondaryCtaURL, isExternalLinkSecondary)
            }
            isBackgroundGreen={isGreenBG}
            isIconLeft={buttonIsWithArrow}
            isYellowBg={isGreenBG}
          >
            {secondaryCtaText}
          </ButtonPrimary>
        ) : null}
        {((primaryCtaText && primaryCtaURL) || feedbackButtonText) && (
          <div
            className={`${secondaryCtaText && secondaryCtaURL ? "ml-4" : ""}`}
            id={feedbackButtonText ? "helpful-article-feedback" : ""}
          >
            <ButtonPrimary
              analytics_key={
                isFormModal
                  ? "formLaunch"
                  : primaryCtaAnalyticsKey || primaryCtaText
              }
              href={tertiaryClickHandler ? `` : primaryCtaURL}
              isExternalLink={isExternalLinkPrimary}
              disabled={isDisabled}
              onClick={(e) => {
                hasFeedbackButtonText
                  ? primaryClickHandler("#", feedbackButtonText, e)
                  : tertiaryClickHandler
                  ? tertiaryClickHandler()
                  : primaryClickHandler(primaryCtaURL, isExternalLinkPrimary)
                analyticsCall()
              }}
              isBackgroundGreen={isGreenBG}
              isIconRight={buttonIsWithArrow}
              isYellowBg={isGreenBG}
            >
              {feedbackButtonText || primaryCtaText}
            </ButtonPrimary>
          </div>
        )}
      </>
    )
  }

  const renderButtonLinkStylewithArrows = (
    secondaryClickHandler,
    tertiaryClickHandler,
  ) => (
    <>
      {secondaryCtaText && secondaryCtaURL && (
        <ButtonArrow
          className={linkStyle}
          analytics_key={secondaryCtaAnalyticsKey || secondaryCtaText}
          href={secondaryClickHandler ? `` : secondaryCtaURL}
          isExternalLink={isExternalLinkSecondary}
          onClick={secondaryClickHandler}
          isLinkYellow={isGreenBG}
          linkStyle={linkStyle}
          showGreenIcon
          useYellowCaret={colorPalette && colorPalette.indexOf("green1") !== -1}
        >
          {secondaryCtaText}
        </ButtonArrow>
      )}
      {primaryCtaText && primaryCtaURL && (
        <div
          className={`${
            secondaryCtaText && secondaryCtaURL
              ? classes.secondaryCtaSpacing
              : ``
          }`}
        >
          <ButtonArrow
            className={linkStyle}
            analytics_key={primaryCtaAnalyticsKey || primaryCtaText}
            href={tertiaryClickHandler ? `` : primaryCtaURL}
            isExternalLink={isExternalLinkPrimary}
            onClick={tertiaryClickHandler}
            isLinkYellow={isGreenBG}
            linkStyle={linkStyle}
            showGreenIcon
            useYellowCaret={
              colorPalette && colorPalette.indexOf("green1") !== -1
            }
          >
            {primaryCtaText}
          </ButtonArrow>
        </div>
      )}
    </>
  )

  const renderButtonLinkStyle = (
    secondaryClickHandler,
    tertiaryClickHandler,
  ) => (
    <>
      {secondaryCtaText && secondaryCtaURL && (
        <ButtonArrow
          analytics_key={secondaryCtaAnalyticsKey || secondaryCtaText}
          href={secondaryClickHandler ? `` : secondaryCtaURL}
          isExternalLink={isExternalLinkSecondary}
          onClick={secondaryClickHandler}
          isBackgroundGreen={isGreenBG}
          isLinkYellow={isGreenBG}
          linkStyle={linkStyle}
        >
          {secondaryCtaText}
        </ButtonArrow>
      )}
      {primaryCtaText && primaryCtaURL && (
        <div
          className={`${
            secondaryCtaText && secondaryCtaURL
              ? classes.secondaryCtaSpacing
              : ``
          }`}
        >
          <ButtonArrow
            analytics_key={primaryCtaAnalyticsKey || primaryCtaText}
            href={tertiaryClickHandler ? `` : primaryCtaURL}
            isExternalLink={isExternalLinkPrimary}
            onClick={tertiaryClickHandler}
            isBackgroundGreen={isGreenBG}
            isLinkYellow={isGreenBG}
            linkStyle={linkStyle}
          >
            {primaryCtaText}
          </ButtonArrow>
        </div>
      )}
    </>
  )

  const renderButtons = () => {
    let secondaryClickHandler
    let tertiaryClickHandler
    let hasFeedbackButtonText

    if (secondaryCtaURL && secondaryCtaURL.indexOf(`#`) === 0) {
      secondaryClickHandler = () => {
        setModalId && setModalId(secondaryCtaURL)
      }
    }
    if (feedbackButtonText) {
      hasFeedbackButtonText = true
    }

    if (primaryCtaURL && primaryCtaURL.indexOf(`#`) === 0) {
      tertiaryClickHandler = () => {
        setModalId && setModalId(primaryCtaURL)
      }
    }
    if (buttonIsLinkStyle) {
      return renderButtonLinkStyle(secondaryClickHandler, tertiaryClickHandler)
    }
    if (buttonIsLinkStylewithArrows) {
      return renderButtonLinkStylewithArrows(
        secondaryClickHandler,
        tertiaryClickHandler,
      )
    }
    return renderButtonWithNWithoutArrows(
      secondaryClickHandler,
      tertiaryClickHandler,
      hasFeedbackButtonText,
    )
  }

  const renderHeading = () => {
    const DesktopHeading = () => (
      <Typography
        data-testid="SingleColumnText - Heading"
        variant={isHeadingH1 ? "h1" : "h2"}
        className={`${classes.heading} supportBlockHeading`}
      >
        {heading}
      </Typography>
    )
    const MobileHeading = () => (
      <Typography
        data-testid="SingleColumnText - Heading"
        variant={
          innerWidth <= BOOTSTRAP_GRID_IPAD_PRO_MAX &&
          innerWidth >= BOOTSTRAP_GRID_MD_MIN
            ? "h3"
            : "h4"
        }
        className={`${classes.heading} maax-font-weight-bold supportBlockHeading`}
      >
        {heading}
      </Typography>
    )

    if (!innerWidth) {
      return <DesktopHeading />
    }

    return (
      <>
        {innerWidth > BOOTSTRAP_GRID_IPAD_PRO_MAX && <DesktopHeading />}
        {innerWidth <= BOOTSTRAP_GRID_IPAD_PRO_MAX && <MobileHeading />}
      </>
    )
  }

  const dataLayerValue = JSON.stringify(data)

  return (
    <div
      className={`${
        supportPage ? classes.supportcheck : classes.root
      }  SingleColumnText`}
      data-testid="SingleColumnText"
      data-cmp-data-layer={dataLayerValue}
      id={componentId?.replace("#", "")}
    >
      <div
        className={`${
          supportPage
            ? `container innercontainer`
            : `container d-flex align-items-${flexAlignment} justify-content-${flexAlignment} ${classes.horizontalScroll}`
        }`}
        ref={scrollContainerRef}
      >
        {enableTargetPersonalization && (
          <div
            id="adobe-target-sct-personalization"
            style={{ display: "none" }}
          />
        )}
        <div
          className={` ${
            supportPage
              ? `${classes.supportInner} `
              : `{ ${
                  classes.innerContainer
                } d-flex flex-column py-4 text-${alignment} align-items-${flexAlignment} ${
                  useFullWidthText
                    ? `w-100 px-0`
                    : classes.fixedWidthSecondaryContainer
                }}`
          }`}
        >
          <Box className={`${useFullWidthText ? `` : classes.fixedWidthBody}`}>
            {heading && renderHeading()}
            {subhead && (
              <Typography
                data-testid="SingleColumnText - subhead"
                variant="h5"
                role="none"
                className={`${classes.subhead} maax-font-weight-bold ${classes.heading}`}
              >
                {reactHtmlParser(subhead)}
              </Typography>
            )}
            {body !== `` ? (
              <Box
                className={`${
                  useFullWidthText ? `fullWidthText` : ``
                } supportCheckBody`}
              >
                <FormattedTextField
                  testid="SingleColumnText - body"
                  setRef={bodyRef}
                  variant="body1"
                  component="div"
                  className={`${
                    classes.singleColumnBody
                  } body-text-container maax-font-weight-regular ${
                    classes.body
                  } ${useFullWidthText ? `` : classes.fixedWidthBody} ${
                    hasGreenBackground ? "yellow-link" : ""
                  }`}
                >
                  {body}
                </FormattedTextField>
              </Box>
            ) : null}
          </Box>
          <div
            data-testid="SingleColumnText - renderButton"
            className={`d-flex ${
              buttonIsLinkStyle
                ? `${classes.buttonAlignment} align-content-${flexAlignment}`
                : ``
            } ${hasGreenBackground} ${
              classes.buttonContainer
            } renderButtonsinMobile${linkStyle} supportCheckButton`}
          >
            {renderButtons()}
          </div>
        </div>
      </div>
    </div>
  )
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// SingleColumnText.propTypes = {
//   /** Title - default to H2 */
//   heading: PropTypes.string,
//   isHeadingH1: PropTypes.bool,
//   subhead: PropTypes.string,
//   body: PropTypes.string,
//   colorPalette: PropTypes.string,
//   secondaryCtaText: PropTypes.string,
//   secondaryCtaURL: PropTypes.string,
//   secondaryCtaAnalyticsKey: PropTypes.string,
//   primaryCtaText: PropTypes.string,
//   primaryCtaURL: PropTypes.string,
//   primaryCtaAnalyticsKey: PropTypes.string,
//   linkStyle: PropTypes.string,
//   isExternalLinkPrimary: PropTypes.bool,
//   isExternalLinkSecondary: PropTypes.bool,
//   alignment: PropTypes.string,
//   useFullWidthText: PropTypes.bool,
//   setModalId: PropTypes.func.isRequired,
//   router: PropTypes.object.isRequired,
//   data: PropTypes.object,
//   isRecycleQuizPage: PropTypes.bool.isRequired,
//   isQuizCompleted: PropTypes.bool.isRequired,
//   feedbackButtonText: PropTypes.string,
//   feedbackCaptureIndicator: PropTypes.string,
//   supportPage: PropTypes.bool,
//   componentId: PropTypes.string,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// SingleColumnText.defaultProps = {
//   subhead: ``,
//   body: ``,
//   alignment: `left`,
//   linkStyle: `buttonwithoutarrows`,
//   colorPalette: `white`,
//   useFullWidthText: false,
//   isHeadingH1: false,
//   feedbackButtonText: "",
//   feedbackCaptureIndicator: "",
//   heading: "",
//   secondaryCtaText: "",
//   secondaryCtaURL: "",
//   secondaryCtaAnalyticsKey: "",
//   primaryCtaText: "",
//   primaryCtaURL: "",
//   primaryCtaAnalyticsKey: "",
//   isExternalLinkPrimary: false,
//   isExternalLinkSecondary: false,
//   data: {},
//   supportPage: false,
//   componentId: null,
// }

export default withAuthentication(withRouter(SingleColumnText))
